import { Tag, Plus, Layers, MapPin, AlertTriangle } from 'lucide-react';
import React from 'react';

const ProductForm = ({ 
    formData, 
    dispatch, 
    categories, 
    locations, 
    filteredAisles, 
    filteredRacks, 
    type, 
    item, 
    categoriesLoading, 
    locationsLoading, 
    coordsLoading, 
    onManageCatalog,
    skuConflict 
}) => {
    return (
        <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-6">
                <h5 className="text-[10px] font-black uppercase tracking-[0.4em] text-primary/40 flex items-center gap-3">
                    <div className="h-[1px] flex-1 bg-gray-100" />
                    Technical Data
                    <div className="h-[1px] flex-1 bg-gray-100" />
                </h5>

                <div className="flex flex-col gap-2">
                    <label htmlFor="inv-sku" className="text-[10px] font-black uppercase tracking-widest text-gray-400 flex items-center gap-2 ml-1">
                        <Tag size={12} className={skuConflict ? 'text-red-500' : 'text-accent'} /> Part Code (SKU)
                        {skuConflict && (
                            <span className="ml-auto text-[9px] font-black uppercase tracking-widest text-red-500 animate-pulse">
                                ⚠ SKU taken
                            </span>
                        )}
                    </label>
                    <div className="relative">
                        <input
                            id="inv-sku"
                            required
                            placeholder="e.g., VALV-SS-001"
                            value={formData.sku}
                            onChange={(e) => dispatch({ type: 'SET_FORM_DATA', value: { sku: String(e.target.value).toUpperCase() } })}
                            disabled={type === 'EDIT' && item?.sku}
                            className={`w-full px-6 py-4 bg-gray-50 border-2 rounded-2xl outline-none font-black text-primary text-lg transition-all disabled:opacity-50 shadow-inner focus:bg-white ${
                                skuConflict
                                    ? 'border-red-400 bg-red-50/40 focus:border-red-500'
                                    : 'border-transparent focus:border-accent/30'
                            }`}
                        />
                    </div>
                    {skuConflict && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-xl">
                            <AlertTriangle size={13} className="text-red-500 shrink-0" />
                            <span className="text-[11px] font-bold text-red-600 leading-tight">
                                A part with SKU <strong>{formData.sku}</strong> already exists in the catalog.
                            </span>
                        </div>
                    )}
                </div>

                <div className="flex flex-col gap-2">
                    <label htmlFor="inv-name" className="text-[10px] font-black uppercase tracking-widest text-gray-400 flex items-center gap-2 ml-1">
                        <Plus size={12} className="text-accent" /> Item Name
                    </label>
                    <input
                        id="inv-name"
                        required
                        placeholder="e.g., Ball Valve, Pressure Gauge..."
                        value={formData.name}
                        onChange={(e) => dispatch({ type: 'SET_FORM_DATA', value: { name: e.target.value } })}
                        className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-accent/30 outline-none font-black text-primary text-lg transition-all shadow-inner"
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <label htmlFor="inv-description" className="text-[10px] font-black uppercase tracking-widest text-gray-400 flex items-center gap-2 ml-1">
                        <Plus size={12} className="text-accent" /> Part Description <span className="text-[9px] text-gray-400/80 font-bold lowercase italic">(optional)</span>
                    </label>
                    <input
                        id="inv-description"
                        placeholder="e.g., Stainless Steel Valve 2 inches"
                        value={formData.description || ''}
                        onChange={(e) => dispatch({ type: 'SET_FORM_DATA', value: { description: e.target.value } })}
                        className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-accent/30 outline-none font-black text-primary text-lg transition-all shadow-inner"
                    />
                </div>

                <div className="flex flex-col gap-2 relative">
                    <label htmlFor="inv-category" className="text-[10px] font-black uppercase tracking-widest text-gray-400 flex items-center justify-between ml-1">
                        <div className="flex items-center gap-2">
                            <Layers size={12} className="text-accent" /> Official Category
                        </div>
                        <button
                            type="button"
                            onClick={() => dispatch({ type: 'SET_FIELD', field: 'isCatManagerOpen', value: true })}
                            className="text-accent hover:underline text-[9px] font-black uppercase tracking-tighter"
                        >
                            [ Manage ]
                        </button>
                    </label>
                    <select
                        id="inv-category"
                        required
                        value={formData.category}
                        onChange={(e) => {
                            const cat = categories.find(c => c.name === e.target.value);
                            dispatch({ type: 'SET_FORM_DATA', value: { category: e.target.value, categoryId: cat?.id || '' } });
                        }}
                        className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-accent/30 outline-none font-black text-primary text-lg transition-all shadow-inner appearance-none cursor-pointer"
                        disabled={categoriesLoading}
                    >
                        <option value="">Select Category...</option>
                        {categories
                            .filter(cat => cat.isActive !== false || cat.name === formData.category)
                            .map(cat => (
                            <option key={cat.id} value={cat.name}>{cat.name}</option>
                        ))}
                    </select>
                    <div className="absolute right-6 bottom-5 pointer-events-none text-slate-400">
                        <Plus size={16} />
                    </div>
                </div>
            </div>

            <div className="flex flex-col gap-6">
                <h5 className="text-[10px] font-black uppercase tracking-[0.4em] text-primary/40 flex items-center gap-3">
                    <div className="h-[1px] flex-1 bg-gray-100" />
                    Location and Stock
                    <div className="h-[1px] flex-1 bg-gray-100" />
                </h5>

                <div className="flex flex-col gap-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="flex flex-col gap-2 relative">
                            <label htmlFor="inv-zone" className="text-[10px] font-black uppercase tracking-widest text-gray-400 flex items-center justify-between ml-1">
                                <div className="flex items-center gap-2">
                                    <MapPin size={12} className="text-accent" /> Zones
                                    <span className="text-[9px] text-red-400 font-bold lowercase italic">(required)</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={onManageCatalog}
                                    className="text-accent hover:underline text-[9px] font-black uppercase tracking-tighter"
                                >
                                    [ Manage ]
                                </button>
                            </label>
                            <select
                                id="inv-zone"
                                required
                                value={formData.location?.section || ''}
                                onChange={(e) => {
                                    const loc = locations.find(l => l.name === e.target.value);
                                    dispatch({
                                        type: 'SET_LOCATION',
                                        value: { 
                                            section: e.target.value,
                                            sectionId: loc?.id || '',
                                            aisle: '', 
                                            aisleId: '',
                                            rack: '',
                                            rackId: ''
                                        }
                                    });
                                }}
                                className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-accent/30 outline-none font-black text-primary text-lg transition-all shadow-inner appearance-none cursor-pointer"
                                disabled={locationsLoading}
                            >
                                <option value="">Select Zone...</option>
                                {locations
                                    .filter(loc => loc.isActive !== false || loc.name === formData.location?.section)
                                    .map(loc => (
                                    <option key={loc.id} value={loc.name}>{loc.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-col gap-2 relative">
                            <label htmlFor="inv-shelf" className="text-[10px] font-black uppercase tracking-widest text-gray-400 flex items-center justify-between ml-1">
                                <div className="flex items-center gap-2">
                                    <Layers size={12} className="text-accent" /> Shelf / Cabinet
                                    <span className="text-[9px] text-red-400 font-bold lowercase italic">(required)</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={onManageCatalog}
                                    className="text-accent hover:underline text-[9px] font-black uppercase tracking-tighter"
                                >
                                    [ Manage ]
                                </button>
                            </label>
                            <select
                                id="inv-shelf"
                                required
                                value={formData.location?.aisle || ''}
                                onChange={(e) => {
                                    const aisle = filteredAisles.find(a => a.name === e.target.value);
                                    dispatch({
                                        type: 'SET_LOCATION',
                                        value: { 
                                            aisle: e.target.value,
                                            aisleId: aisle?.id || '',
                                            rack: '',
                                            rackId: ''
                                        }
                                    });
                                }}
                                className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-accent/30 outline-none font-black text-primary text-lg transition-all shadow-inner appearance-none cursor-pointer"
                                disabled={coordsLoading || !formData.location.section}
                            >
                                <option value="">Select Shelf...</option>
                                {filteredAisles
                                    .filter(a => a.isActive !== false || a.name === formData.location?.aisle)
                                    .map(a => (
                                    <option key={a.id} value={a.name}>{a.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                        <div className="flex flex-col gap-2 relative">
                            <label htmlFor="inv-level" className="text-[10px] font-black uppercase tracking-widest text-gray-400 flex items-center gap-2 ml-1">
                                Floor / Level
                                <span className="text-[9px] text-red-400 font-bold lowercase italic">(required)</span>
                            </label>
                            <select
                                id="inv-level"
                                required
                                value={formData.location?.rack || ''}
                                onChange={(e) => {
                                    const rack = filteredRacks.find(r => r.name === e.target.value);
                                    dispatch({
                                        type: 'SET_LOCATION',
                                        value: { 
                                            rack: e.target.value,
                                            rackId: rack?.id || ''
                                        }
                                    });
                                }}
                                className="w-full px-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-accent/30 outline-none font-black text-primary text-sm transition-all shadow-inner appearance-none cursor-pointer"
                                disabled={coordsLoading || !formData.location.aisle}
                            >
                                <option value="">Select Floor...</option>
                                {filteredRacks
                                    .filter(r => r.isActive !== false || r.name === formData.location?.rack)
                                    .map(r => (
                                    <option key={r.id} value={r.name}>{r.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-col gap-2">
                            <label htmlFor="inv-bin" className="text-[10px] font-black uppercase tracking-widest text-gray-400 flex items-center gap-2 ml-1">
                                Bin / Detail
                                <span className="text-[9px] text-red-400 font-bold lowercase italic">(required)</span>
                            </label>
                            <input
                                id="inv-bin"
                                required
                                placeholder="Bin B"
                                value={formData.location?.container || ''}
                                onChange={(e) => dispatch({
                                    type: 'SET_LOCATION',
                                    value: { container: e.target.value }
                                })}
                                className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-accent/30 outline-none font-black text-primary transition-all shadow-inner"
                            />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                    <div className="flex flex-col gap-3">
                        <label htmlFor="inv-stock-current" className="text-[10px] font-black text-gray-400 ml-1 italic">
                            {type === 'EDIT' ? 'CURRENT INVENTORY' : 'INITIAL INVENTORY'}
                            {type === 'ADD' && <span className="text-[9px] text-red-400 font-bold lowercase italic ml-1">(required)</span>}
                        </label>
                        <input 
                            id="inv-stock-current" 
                            type="number" 
                            min="0"
                            required={type === 'ADD'}
                            placeholder={type === 'ADD' ? "e.g., 10" : ""}
                            value={formData.stock_current} 
                            onChange={(e) => dispatch({ type: 'SET_FORM_DATA', value: { stock_current: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10)) } })} 
                            disabled={type === 'EDIT'} 
                            className="px-5 py-4 bg-primary/5 border-2 border-transparent rounded-2xl font-black text-primary text-xl outline-none focus:bg-white focus:border-primary/20 shadow-inner disabled:opacity-60 disabled:cursor-not-allowed" 
                        />
                    </div>
                    <div className="flex flex-col gap-3">
                        <label htmlFor="inv-stock-min" className="text-[10px] font-black text-gray-400 ml-1 italic tracking-widest leading-none text-red-400">MINIMUM STOCK</label>
                        <input id="inv-stock-min" type="number" min="0" value={formData.stock_min} onChange={(e) => dispatch({ type: 'SET_FORM_DATA', value: { stock_min: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10)) } })} className="px-5 py-4 bg-red-50/50 border-2 border-transparent rounded-2xl font-black text-red-500 text-xl outline-none focus:bg-white focus:border-red-500/20 shadow-inner" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductForm;
